<template>
  <div class="donut-chart__container">
    <div
      v-show="loading"
      class="donut-chart-skeleton"
    >
      <v-skeleton-loader
        type="image"
        class="donut-chart-skeleton__pie"
      />
      <div class="skeleton-legend">
        <v-skeleton-loader
          v-for="index in 4"
          :key="index"
          type="button"
          class="skeleton-legend__item"
          width="200"
          height="16"
        />
      </div>
    </div>

    <highcharts
      v-show="!loading"
      ref="chartRef"
      :callback="onChartReady"
      :options="donutChartOptions"
    />
  </div>
</template>

<script setup lang="ts">
import Vue, { computed, ref } from 'vue';
import HighchartsVue from 'highcharts-vue';
import Theme from '../service/themes.js';

Vue.use(HighchartsVue);

const props = withDefaults(
  defineProps<{
    ofText: string;
    loading?: boolean;
    options: object;
    isSelectable?: boolean;
  }>(),
  {
    loading: false,
    isSelectable: false,
  }
);

const emit = defineEmits<{
  (e: 'chartReady'): void;
  (e: 'select', items: string[]): void;
}>();

defineExpose({ clearSelection });

const theme = new Theme();

const selectedItems = ref<string[]>([]);
const chartRef = ref<Highcharts.Chart>();

const donutChartOptions = computed(() => {
  const config = {
    ...donutChartDefaultOptions,
    ...props.options,
  };

  config.chart.animation = false;
  config.plotOptions.series.animation = false;
  return config;
});

const donutChartDefaultOptions = {
  chart: {
    type: 'pie',
    height: 140,
    width: 265,
    backgroundColor: 'transparent',
    animation: false, // Disable animation at the chart level
    marginRight: 130,
    marginLeft: 0,
    marginBottom: 0,
    marginTop: 0,
    paddingTop: 0,
    spacingTop: 0,
    spacingRight: 0,
    spacingBottom: 0,
    spacingLeft: 0,
    plotBorderWidth: 0,
    margin: [0, 0, 0, 0],
    style: {
      fontFamily: 'inherit',
    },
  },
  yAxis: {
    minPadding: 0,
    maxPadding: 0,
  },
  legend: {
    align: 'right',
    verticalAlign: 'middle',
    layout: 'vertical',
    itemMarginTop: 2,
    itemMarginBottom: 2,
    itemStyle: {
      fontSize: '12px',
      fontWeight: '400',
      fontFamily: 'inherit',
    },
    itemWidth: 120, // this is the best value for item width to fit the text
    symbolHeight: 0,
    symbolWidth: 0,
    labelFormatter: function () {
      return `<span style="color:${this.color}; font-family: inherit;"> ${this.y} </span> <span class="donut-chart__label" style="font-family: inherit;">${this.name}</span>`;
    },
    events: {
      itemClick: function () {
        // Prevent default action on legend item click
        return false;
      },
    },
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    pie: {
      shadow: false,
      center: ['50%', '50%'],
      dataLabels: {
        enabled: false,
      },
      borderRadius: 0,
      borderWidth: 1,
      showInLegend: true,
      size: '100%',
      allowPointSelect: props.isSelectable,
      cursor: 'pointer',
      point: {
        events: {
          click: function () {
            if (props.isSelectable) {
              this.slice(null); // Toggle pull-out/in effect
              this.select(null, true); // Toggle selection
              // Handle selection manually
              toggleSelection(this.name, this.y);
            }
          },
        },
      },
    },
    series: {
      animation: false, // Ensure animation is disabled for series
      size: '100%',
      innerSize: '60%',
      states: {
        hover: {
          halo: false,
        },
      },
      point: {
        events: {
          legendItemClick: function (e) {
            // Prevent default action on legend item click and handle selection manually
            e.preventDefault();
            if (props.isSelectable) {
              this.slice(null); // Toggle pull-out/in effect
              this.select(null, true); // Toggle selection
              // Handle selection manually
              toggleSelection(this.name, this.y);
            }
          },
        },
      },
    },
  },
  exporting: {
    enabled: false,
  },
  title: {
    text: '',
  },
  tooltip: {
    useHTML: true,
    valueDecimals: 2,
    padding: 8,
    split: true,
    shared: true,
    borderRadius: 8,
    borderWidth: 1,
    backgroundColor: theme.defaultColors.primaryWhite,
    borderColor: theme.defaultColors.elements,
    shadow: false,
    style: {
      fontSize: '12px',
      color: theme.defaultColors.secondary,
      fontFamily: 'inherit',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '12px',
    },
    formatter: function () {
      return `${this.key} ${this.y}`;
    },
  },
};

function clearSelection() {
  const chart = chartRef.value?.chart;
  if (!chart) return;
  const points = chart.series[0].points;
  points.forEach((point) => {
    point.select(false, true);
    point.slice(false);
  });
  selectedItems.value = [];
  emit('select', []);
}

function toggleSelection(item: string, value: number) {
  const items = selectedItems.value;
  const index = items.indexOf(item);

  if (index > -1) {
    items.splice(index, 1);
  } else if (value > 0) {
    items.push(item);
  }

  emit('select', [...items]);
}

function onChartReady() {
  emit('chartReady');
}
</script>

<style lang="scss">
@import '../assets/styles/main';

.donut-chart {
  &__container {
    display: flex;
  }
  &__skeleton {
    height: 100%;
    width: 100%;
  }
  &__label {
    @include body-3;
    font-family: inherit;
  }
}
.inner-info {
  @include subtitle-1;
  color: $primary;
  &__title {
    font-size: 10px;
    font-weight: 700;
  }
  &__text {
    display: flex;
    font-size: 8px;
    font-weight: 400;
  }
}

.donut-chart-skeleton {
  display: flex;
  gap: 10px;
  padding: 10px;
  &__pie {
    height: 90px;
    width: 90px;
    border-radius: 92px;
  }
}

.skeleton-legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
  &__item {
    height: 16px;
    width: 109px;
  }
}
</style>
